function stripDataUrlPrefix(value: string): string {
  const marker = 'base64,';
  const index = value.indexOf(marker);
  return index >= 0 ? value.slice(index + marker.length) : value;
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary);
}

export function captureVideoFrameAsPngBase64(video: HTMLVideoElement): string {
  if (!video || video.videoWidth <= 0 || video.videoHeight <= 0) {
    throw new Error('no-playable-frame');
  }

  return captureDrawableAsPngBase64(video, video.videoWidth, video.videoHeight);
}

export function captureImageElementAsPngBase64(image: HTMLImageElement): string {
  if (!image?.complete || image.naturalWidth <= 0 || image.naturalHeight <= 0) {
    throw new Error('no-playable-frame');
  }

  return captureDrawableAsPngBase64(image, image.naturalWidth, image.naturalHeight);
}

export async function captureFetchedImageAsPngBase64(blob: Blob): Promise<string> {
  if (!blob?.size) {
    throw new Error('no-playable-frame');
  }

  const objectUrl = URL.createObjectURL(blob);
  try {
    const image = await loadImageElement(objectUrl);
    return captureImageElementAsPngBase64(image);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function loadImageElement(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('no-playable-frame'));
    image.src = src;
  });
}

function captureDrawableAsPngBase64(
  source: CanvasImageSource,
  width: number,
  height: number,
): string {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  try {
    const context = canvas.getContext('2d');
    if (context) {
      context.drawImage(source, 0, 0, width, height);
    }
  } catch {
    // jsdom has no 2d context; toDataURL may still be stubbed in tests.
  }
  try {
    return stripDataUrlPrefix(canvas.toDataURL('image/png'));
  } catch {
    throw new Error('tainted-canvas');
  }
}

const ALARM_CLIP_DURATION_MS = 2000;

export async function captureVideoClipAsBase64(video: HTMLVideoElement): Promise<string> {
  if (!video || video.videoWidth <= 0 || video.videoHeight <= 0) {
    throw new Error('no-playable-clip');
  }

  const stream = capturePlaybackStream(video);
  const MediaRecorderCtor = globalThis.MediaRecorder;
  if (!stream || typeof MediaRecorderCtor !== 'function') {
    throw new Error('no-playable-clip');
  }

  const chunks: Blob[] = [];
  const recorder = new MediaRecorderCtor(stream);
  const recorded = new Promise<Blob>((resolve, reject) => {
    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        chunks.push(event.data);
      }
    };
    recorder.onerror = () => reject(new Error('no-playable-clip'));
    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: recorder.mimeType || 'video/webm' });
      if (!blob.size) {
        reject(new Error('no-playable-clip'));
        return;
      }
      resolve(blob);
    };
  });

  recorder.start();
  await Promise.resolve();
  await Promise.race([recorded.then(() => undefined), waitForClipWindow()]);
  if (recorder.state !== 'inactive') {
    recorder.stop();
  }

  const buffer = await (await recorded).arrayBuffer();
  if (!buffer.byteLength) {
    throw new Error('no-playable-clip');
  }
  return bytesToBase64(new Uint8Array(buffer));
}

function capturePlaybackStream(video: HTMLVideoElement): MediaStream {
  const withCapture = video as HTMLVideoElement & {
    captureStream?: () => MediaStream;
    mozCaptureStream?: () => MediaStream;
  };
  try {
    const captured = withCapture.captureStream?.() ?? withCapture.mozCaptureStream?.();
    if (captured) {
      return captured;
    }
  } catch {
    // jsdom and some stream backends have no captureStream.
  }

  const StreamCtor = (globalThis as { MediaStream?: { new (): MediaStream } }).MediaStream;
  if (typeof StreamCtor === 'function') {
    try {
      return new StreamCtor();
    } catch {
      // Fall through to a dummy stream for MediaRecorder stubs.
    }
  }

  return {} as MediaStream;
}

function waitForClipWindow(): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ALARM_CLIP_DURATION_MS);
  });
}
